import jsPDF from "jspdf";
import { saveAs } from "file-saver";
import { meetingMediaUrl } from "../../api/Meetings/meetingSessionsApi";
import { getFilesByMuestraId } from "../../api/Muestras/muestraApi";
import { getFileContent } from "../../api/Design/fileApi";
import logoMatrix6 from "../../img/logos/Matrix6.png";
import {
  chunkLeaveSamplePages,
  leaveSampleCardHeight,
  leaveSampleClienteName,
  leaveSamplePageRows,
  leaveSamplePdfCaption,
} from "./meetingLeaveSamplesPdfLabel";

const PAGE = { width: 595.28, height: 841.89 }; // A4 portrait (pt)
const MARGIN = 36;
/** Cyan del anillo Matrix6 */
const ACCENT = [0, 188, 196];
const INK = [28, 32, 36];
const MUTED = [110, 118, 126];
const FOOTER_CONTACT =
  "Stage Design Studio · Irène Guijt | irene@stagedesign.es | +34 667 765 007";

function loadImageSrc(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

/**
 * Cabecera: anillo Matrix6 + STAGE / DESIGN / STUDIO.
 * Devuelve la Y bajo el bloque.
 */
async function drawBrandHeader(doc, left, right) {
  const logoSize = 52;
  const logoTop = 22;
  try {
    const logo = await loadImageSrc(logoMatrix6);
    doc.addImage(logo, "PNG", left, logoTop, logoSize, logoSize);
  } catch (err) {
    console.warn("leave-samples PDF logo:", err?.message || err);
  }

  const textX = left + logoSize + 14;
  const gray = 100;
  const size = 15;
  const lineH = 17;
  doc.setFont("helvetica", "normal");
  doc.setFontSize(size);
  doc.setTextColor(gray, gray, gray);
  const baseline = logoTop + 14;
  doc.text("STAGE", textX, baseline);
  doc.text("DESIGN", textX, baseline + lineH);
  doc.text("STUDIO", textX, baseline + lineH * 2);
  doc.setTextColor(0);

  const y = logoTop + logoSize + 14;
  doc.setDrawColor(...ACCENT);
  doc.setLineWidth(1.4);
  doc.line(left, y, right, y);
  return y + 18;
}

function isImagePhoto(photo) {
  if (!photo) return false;
  if (photo.kind === "video") return false;
  const mime = String(photo.mimeType || "").toLowerCase();
  if (mime.startsWith("video/")) return false;
  return Boolean(photo.fileUrl);
}

function resolveLeavePhoto(item, photos = []) {
  const mid = Number(item?.idMuestra);
  const list = Array.isArray(photos) ? photos.filter(isImagePhoto) : [];

  if (item?.fileUrl) {
    return { fileUrl: item.fileUrl, id: item.photoId };
  }
  if (item?.photoId != null) {
    const byId = list.find((p) => Number(p.id) === Number(item.photoId));
    if (byId?.fileUrl) return byId;
  }

  const matches = list.filter(
    (p) =>
      Number(p.idMuestra) === mid ||
      (Array.isArray(p.linkedMuestras) &&
        p.linkedMuestras.some((l) => Number(l.idMuestra) === mid)),
  );
  if (!matches.length) return null;

  // Prefer leave-* copies from leave-samples, then any linked image
  const leaveCopy = matches.find((p) =>
    /\/leave-m\d+/i.test(String(p.fileUrl || "")),
  );
  return leaveCopy || matches[0];
}

async function blobToJpegDataUrl(blob, maxEdge = 900) {
  if (!blob) return null;
  const objectUrl = URL.createObjectURL(blob);
  try {
    const img = await loadImageSrc(objectUrl);
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return null;
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, cw, ch);
    ctx.drawImage(img, 0, 0, cw, ch);
    return {
      dataUrl: canvas.toDataURL("image/jpeg", 0.88),
      width: cw,
      height: ch,
    };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

async function base64ToJpegDataUrl(
  base64,
  mimeType = "image/jpeg",
  maxEdge = 900,
) {
  if (!base64) return null;
  const dataUrl = `data:${mimeType || "image/jpeg"};base64,${base64}`;
  try {
    const img = await loadImageSrc(dataUrl);
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    if (!w || !h) return null;
    const scale = Math.min(1, maxEdge / Math.max(w, h));
    const cw = Math.max(1, Math.round(w * scale));
    const ch = Math.max(1, Math.round(h * scale));
    const canvas = document.createElement("canvas");
    canvas.width = cw;
    canvas.height = ch;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, cw, ch);
    ctx.drawImage(img, 0, 0, cw, ch);
    return {
      dataUrl: canvas.toDataURL("image/jpeg", 0.88),
      width: cw,
      height: ch,
    };
  } catch (err) {
    console.warn("leave-samples PDF base64:", err?.message || err);
    return null;
  }
}

/** Intenta leer la foto del feed de reunión (necesita CORS en /meeting-sessions). */
async function imageUrlToJpegDataUrl(url) {
  if (!url) return null;
  try {
    const res = await fetch(url, { mode: "cors", credentials: "omit" });
    if (!res.ok) return null;
    return await blobToJpegDataUrl(await res.blob());
  } catch (err) {
    console.warn("leave-samples PDF meeting image:", err?.message || err);
    return null;
  }
}

function isImageFileName(name = "") {
  return /\.(jpe?g|png|webp|gif|bmp)$/i.test(String(name));
}

function pickMuestraPhotoFile(files = []) {
  const list = Array.isArray(files) ? files : [];
  const images = list.filter((f) => {
    const name = f?.name || f?.nombre || "";
    const path = f?.path || "";
    return isImageFileName(name) || isImageFileName(path);
  });
  if (!images.length) return null;
  // Prefer root sample photo (no category / not Calidades-Print)
  const root = images.find((f) => !f.category && !f.Category);
  return root || images[0];
}

/**
 * Fallback autenticado: foto de la carpeta de la muestra (funciona sin CORS static).
 */
async function loadMuestraCatalogJpeg(idMuestra) {
  const mid = Number(idMuestra);
  if (!Number.isInteger(mid) || mid <= 0) return null;
  try {
    const files = await getFilesByMuestraId(mid);
    const pick = pickMuestraPhotoFile(files);
    if (!pick?.path) return null;
    const response = await getFileContent(pick.path);
    const { mimeType, data } = response?.data || {};
    if (!data) return null;
    if (mimeType && !String(mimeType).startsWith("image/")) return null;
    return await base64ToJpegDataUrl(data, mimeType || "image/jpeg");
  } catch (err) {
    console.warn("leave-samples PDF muestra catalog:", err?.message || err);
    return null;
  }
}

async function resolveLeaveItemImage(item, photos = []) {
  const photo = resolveLeavePhoto(item, photos);
  if (photo?.fileUrl) {
    const fromMeeting = await imageUrlToJpegDataUrl(
      meetingMediaUrl(photo.fileUrl),
    );
    if (fromMeeting) return fromMeeting;
  }
  return loadMuestraCatalogJpeg(item?.idMuestra);
}

function fitContain(srcW, srcH, boxW, boxH) {
  if (!srcW || !srcH) return { w: boxW, h: boxH, x: 0, y: 0 };
  const scale = Math.min(boxW / srcW, boxH / srcH);
  const w = srcW * scale;
  const h = srcH * scale;
  return { w, h, x: (boxW - w) / 2, y: (boxH - h) / 2 };
}

function formatDateEs(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value).slice(0, 10);
  return d.toLocaleDateString("es-ES", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

/**
 * PDF de entrega: prendas que se queda el cliente (leftWithClient).
 */
export async function downloadMeetingLeaveSamplesPdf({
  session,
  leftWithClient = [],
  photos = [],
} = {}) {
  const items = Array.isArray(leftWithClient) ? leftWithClient : [];
  if (!items.length) {
    throw new Error("No hay prendas marcadas como «se quedan»");
  }

  const enriched = await Promise.all(
    items.map(async (item) => {
      const img = await resolveLeaveItemImage(item, photos);
      return {
        idMuestra: item.idMuestra,
        name: leaveSamplePdfCaption({
          ...item,
          muestraNombreCliente: leaveSampleClienteName(item, photos),
        }),
        leftAt: item.leftAt,
        img,
      };
    }),
  );

  const doc = new jsPDF({
    orientation: "portrait",
    unit: "pt",
    format: "a4",
  });
  const { width: W, height: H } = PAGE;
  const left = MARGIN;
  const right = W - MARGIN;
  const contentW = right - left;

  doc.setFillColor(...ACCENT);
  doc.rect(0, 0, W, 6, "F");

  let y = await drawBrandHeader(doc, left, right);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(17);
  doc.setTextColor(...INK);
  doc.text("Prendas que se queda el cliente", left, y);
  y += 16;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  const meta = [
    session?.title || "Reunión",
    session?.customerName || "",
    formatDateEs(session?.meetingDate || session?.startedAt),
    `${enriched.length} prenda${enriched.length === 1 ? "" : "s"}`,
  ]
    .filter(Boolean)
    .join("  ·  ");
  doc.text(meta, left, y);
  y += 10;

  doc.setFontSize(9);
  doc.text("Documento de entrega · Stage Design Studio", left, y);
  y += 16;

  doc.setFillColor(245, 252, 253);
  doc.setDrawColor(200, 230, 232);
  doc.roundedRect(left, y, contentW, 36, 4, 4, "FD");
  doc.setTextColor(...MUTED);
  doc.setFontSize(9);
  const blurb =
    "Listado de muestras / prendas que el cliente se lleva. Conservad este documento como comprobante de entrega.";
  const blurbLines = doc.splitTextToSize(blurb, contentW - 20);
  doc.text(blurbLines, left + 10, y + 14);
  y += 50;

  const cols = 2;
  const gap = 8;
  const cardW = (contentW - gap) / cols;
  const gridBottom = H - 48;
  const pages = chunkLeaveSamplePages(enriched.length);

  pages.forEach((page, pageIndex) => {
    const top = pageIndex === 0 ? y : 28;
    if (pageIndex > 0) {
      doc.addPage();
      doc.setFillColor(...ACCENT);
      doc.rect(0, 0, W, 5, "F");
    }
    const rows = leaveSamplePageRows(pageIndex);
    const cardH = leaveSampleCardHeight({
      top,
      bottom: gridBottom,
      rows,
      gap,
    });
    const pad = 6;
    const captionH = 22;
    const imgBoxH = Math.max(40, cardH - pad * 2 - captionH);
    const slice = enriched.slice(page.start, page.start + page.count);

    slice.forEach((item, i) => {
      const index = page.start + i;
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = left + col * (cardW + gap);
      const rowY = top + row * (cardH + gap);

      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(220, 226, 228);
      doc.setLineWidth(0.8);
      doc.roundedRect(x, rowY, cardW, cardH, 5, 5, "FD");

      const boxX = x + pad;
      const boxY = rowY + pad;
      const boxW = cardW - pad * 2;
      doc.setFillColor(245, 247, 248);
      doc.roundedRect(boxX, boxY, boxW, imgBoxH, 3, 3, "F");

      if (item.img?.dataUrl) {
        const fit = fitContain(
          item.img.width,
          item.img.height,
          boxW - 4,
          imgBoxH - 4,
        );
        doc.addImage(
          item.img.dataUrl,
          "JPEG",
          boxX + 2 + fit.x,
          boxY + 2 + fit.y,
          fit.w,
          fit.h,
        );
      } else {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(...MUTED);
        doc.text("Sin foto", boxX + boxW / 2, boxY + imgBoxH / 2, {
          align: "center",
        });
      }

      const textY = boxY + imgBoxH + 14;
      doc.setFont("helvetica", "bold");
      doc.setFontSize(9);
      doc.setTextColor(...INK);
      const nameLines = doc.splitTextToSize(item.name, boxW);
      doc.text(nameLines.slice(0, 1), boxX, textY);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(...MUTED);
      doc.text(`#${index + 1}`, x + cardW - pad, rowY + 12, {
        align: "right",
      });
    });
  });

  const pageCount = doc.getNumberOfPages();
  for (let p = 1; p <= pageCount; p += 1) {
    doc.setPage(p);
    doc.setDrawColor(...ACCENT);
    doc.setLineWidth(0.8);
    doc.line(left, H - 34, right, H - 34);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(FOOTER_CONTACT, left, H - 20);
    doc.text(`${p} / ${pageCount}`, right, H - 20, { align: "right" });
  }

  const safeTitle = String(session?.title || "reunion")
    .replace(/[^\w\-]+/g, "_")
    .slice(0, 40);
  const filename = `Se_quedan_${safeTitle}_${new Date()
    .toISOString()
    .slice(0, 10)}.pdf`;
  const blob = doc.output("blob");
  saveAs(blob, filename);
  return { filename, count: enriched.length };
}
